import { useTheme } from '@emotion/react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import Flex from 'shopper/components/Flex';
import Grid from 'shopper/components/Grid';
import Cell from 'shopper/components/Grid/Cell';
import Heading from 'shopper/components/Heading';

import Image from 'components/Image';

import { sendEvent } from 'lib/gtag';
import { getOfferImageLink, getOfferQrCodeSourcePath } from 'lib/image';
import { hasBadge } from 'lib/offer';
import { getUtmsFromSession } from 'lib/sessionStorage';
import { sliceWithEllipsis } from 'lib/string';

import { useUtm } from 'providers/UtmProvider';

import GoToStoreButton from './GoToStoreButton';
import OfferPrice from './OfferPrice';
import OfferTags from './OfferTags';
import StoreAvatar from './StoreAvatar';

import OFFER from 'constants/offer';

const FinishedOfferButtons = dynamic(() => import('./FinishedOfferButtons'));
const OfferBadges = dynamic(() => import('./OfferBadges'));

const OfferShowcase = ({
  gaEventCategory,
  goToStoreButtonRef,
  mostRelevantKeyword,
  offerCoupon,
  offerId,
  offerIsHighlight,
  offerOldPrice,
  offerPhoto,
  offerPrice,
  offerPriceType,
  offerQrCode,
  offerStatusName,
  offerTags,
  offerTitle,
  storeDomain,
  storeImage,
}) => {
  const theme = useTheme();
  const { isUtmLoaded } = useUtm();
  const [utms, setUtms] = useState('');

  useEffect(() => {
    setUtms(new URLSearchParams(getUtmsFromSession()).toString());
  }, [isUtmLoaded]);

  const onOfferImageClick = () =>
    sendEvent({ action: 'image', category: gaEventCategory });

  const onOfferTitleClick = () =>
    sendEvent({ action: 'title', category: gaEventCategory });

  return (
    <Grid
      gridTemplateColumns={['repeat(4, 1fr)', null, 'repeat(12, 1fr)']}
      gridAutoFlow={['row', null, 'column']}
      gridGap={[0, null, '1rem']}
    >
      <Cell
        alignItems="center"
        justifyContent="center"
        gridColumnEnd="span 4"
        bg={theme.app.offer.imageBg}
        borderRight={[null, null, '1px solid']}
        borderY={['1px solid', null, 0]}
        borderColor={[
          theme.app.offer.dividerBg,
          theme.app.offer.dividerBg,
          theme.app.offer.dividerBg,
          theme.app.offer.dividerBg,
        ]}
        height={['fit-content', null, '100%']}
        width={['100%', null, 300]}
      >
        <Image
          sizes="(max-width: 768px) 168px"
          loading={null}
          alt={offerTitle}
          p={4}
          grayscale={offerStatusName === OFFER.STATUSES.FINISHED}
          src={getOfferImageLink(offerPhoto, 268)}
          srcSet={`${getOfferImageLink(
            offerPhoto,
            168
          )} 168w, ${getOfferImageLink(offerPhoto, 268)} 268w`}
          height={[200, null, 300]}
          width={[200, null, 300]}
          onClick={onOfferImageClick}
          priority
        />
      </Cell>
      <Cell
        height="100%"
        gridColumnEnd={['span 4', null, 'span 8']}
        pl={[null, null, 1]}
      >
        <Grid
          height="100%"
          gridTemplateColumns={['repeat(4, 1fr)', null, 'repeat(8, 1fr)']}
          gridGap={[null, null, '1rem']}
          pr={[4, null, 6]}
          pl={[4, null, 0]}
          py={[4, null, 7]}
        >
          <Cell gridColumnEnd={['span 4', null, 'span 5']}>
            <Flex
              flexDirection="column"
              justifyContent="space-between"
              height="100%"
            >
              {hasBadge(offerIsHighlight, offerStatusName) && (
                <OfferBadges
                  offerIsHighlight={offerIsHighlight}
                  offerStatusName={offerStatusName}
                />
              )}
              {offerTitle && (
                <Heading size="size4" as="h1" onClick={onOfferTitleClick}>
                  {sliceWithEllipsis(offerTitle, 180)}
                </Heading>
              )}
              {offerTags.length > 0 && (
                <OfferTags offerTitle={offerTitle} offerTags={offerTags} />
              )}
              <OfferPrice
                offerPrice={offerPrice}
                offerOldPrice={offerOldPrice}
                offerPriceType={offerPriceType}
              />
            </Flex>
          </Cell>
          <Cell gridColumnEnd={['span 4', null, 'span 3']}>
            <Flex
              flexDirection="column"
              justifyContent="space-between"
              alignItems="center"
              height="100%"
            >
              <StoreAvatar
                gaEventCategory={gaEventCategory}
                storeImage={storeImage}
                storeDomain={storeDomain}
                mb={
                  offerQrCode && !offerCoupon
                    ? [6, null, null, 7]
                    : [null, null, null, 11]
                }
                mr={2}
              />
              {offerStatusName === OFFER.STATUSES.FINISHED && (
                <FinishedOfferButtons
                  gaEventCategory={gaEventCategory}
                  offerId={offerId}
                  utms={utms}
                  mostRelevantKeyword={mostRelevantKeyword}
                  mb={[null, null, null, 50]}
                />
              )}
              {offerQrCode && !offerCoupon && (
                <Image
                  src={getOfferQrCodeSourcePath(offerQrCode)}
                  alt="QR Code"
                  height={90}
                  width={90}
                  display={['none', null, null, 'block']}
                  mb={7}
                  priority
                />
              )}
              {offerStatusName !== OFFER.STATUSES.FINISHED && (
                <GoToStoreButton
                  ref={goToStoreButtonRef}
                  offerCoupon={offerCoupon}
                  offerId={offerId}
                  offerQrCode={offerQrCode}
                  offerStatusName={offerStatusName}
                  utms={utms}
                />
              )}
            </Flex>
          </Cell>
        </Grid>
      </Cell>
    </Grid>
  );
};

OfferShowcase.defaultProps = {
  offerCoupon: null,
  offerOldPrice: null,
  utms: null,
  storeImage: null,
};

OfferShowcase.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  goToStoreButtonRef: PropTypes.shape().isRequired,
  mostRelevantKeyword: PropTypes.string.isRequired,
  offerCoupon: PropTypes.string,
  offerId: PropTypes.number.isRequired,
  offerIsHighlight: PropTypes.bool.isRequired,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string.isRequired,
  offerPrice: PropTypes.number.isRequired,
  offerPriceType: PropTypes.string.isRequired,
  offerStatusName: PropTypes.string.isRequired,
  offerQrCode: PropTypes.string.isRequired,
  offerTags: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  offerTitle: PropTypes.string.isRequired,
  storeDomain: PropTypes.string.isRequired,
  storeImage: PropTypes.string,
  utms: PropTypes.string,
};

export default OfferShowcase;
